import React, { useState } from 'react';
import { Search, Shield, FileText, Users, AlertCircle } from 'lucide-react';

const PolicyContentIntentDemo = () => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [showVectors, setShowVectors] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  // Policy content with intent vectors - representing semantic meaning and purpose
  const policyItems = [
    { 
      id: 1, 
      type: 'policy', 
      name: 'General Liability - Bodily Injury Coverage', 
      vector: [0.9, 0.8, 0.3, 0.7, 0.2], 
      category: 'General Liability',
      content: "Covers bodily injury claims from third parties during construction operations",
      intent: "Third-party injury protection",
      similarity: 0 
    },
    { 
      id: 2, 
      type: 'endorsement', 
      name: 'Contractual Liability Endorsement', 
      vector: [0.8, 0.7, 0.4, 0.8, 0.3], 
      category: 'General Liability',
      content: "Extends coverage for liability assumed under construction contracts",
      intent: "Contract obligation coverage",
      similarity: 0 
    },
    { 
      id: 3, 
      type: 'policy', 
      name: 'Workers Compensation - Medical Benefits', 
      vector: [0.7, 0.9, 0.1, 0.4, 0.8], 
      category: 'Workers Compensation',
      content: "Covers medical expenses for employee workplace injuries",
      intent: "Employee injury medical protection",
      similarity: 0 
    },
    { 
      id: 4, 
      type: 'policy', 
      name: 'General Liability - Property Damage', 
      vector: [0.8, 0.6, 0.9, 0.7, 0.2], 
      category: 'General Liability',
      content: "Covers damage to third-party property during construction",
      intent: "Property damage protection",
      similarity: 0 
    },
    { 
      id: 5, 
      type: 'endorsement', 
      name: 'Additional Insured - Owners/Contractors', 
      vector: [0.9, 0.5, 0.7, 0.9, 0.4], 
      category: 'General Liability',
      content: "Extends liability coverage to project owners and general contractors",
      intent: "Stakeholder protection extension",
      similarity: 0 
    },
    { 
      id: 6, 
      type: 'policy', 
      name: 'Workers Comp - Wage Replacement', 
      vector: [0.6, 0.8, 0.2, 0.5, 0.9], 
      category: 'Workers Compensation',
      content: "Provides income benefits for injured workers unable to work",
      intent: "Employee income protection",
      similarity: 0 
    },
    { 
      id: 7, 
      type: 'endorsement', 
      name: 'Waiver of Subrogation', 
      vector: [0.7, 0.4, 0.6, 0.8, 0.5], 
      category: 'Workers Compensation',
      content: "Prevents insurer from pursuing recovery against other parties",
      intent: "Subrogation rights limitation",
      similarity: 0 
    },
    { 
      id: 8, 
      type: 'policy', 
      name: 'General Liability - Personal Injury', 
      vector: [0.8, 0.9, 0.2, 0.6, 0.3], 
      category: 'General Liability',
      content: "Covers claims for false arrest, wrongful eviction, defamation",
      intent: "Non-physical injury protection",
      similarity: 0 
    }
  ];

  const [items, setItems] = useState(policyItems);

  const calculateSimilarity = (vec1, vec2) => {
    const dotProduct = vec1.reduce((sum, val, i) => sum + val * vec2[i], 0);
    const magnitude1 = Math.sqrt(vec1.reduce((sum, val) => sum + val * val, 0));
    const magnitude2 = Math.sqrt(vec2.reduce((sum, val) => sum + val * val, 0));
    return dotProduct / (magnitude1 * magnitude2);
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setShowVectors(true);
    
    // Calculate similarities based on content intent
    const updatedItems = items.map(policyItem => ({
      ...policyItem,
      similarity: calculateSimilarity(item.vector, policyItem.vector)
    }));
    
    setItems(updatedItems);
    setSearchResults(updatedItems.sort((a, b) => b.similarity - a.similarity).slice(0, 5));
  };

  const getIcon = (type) => {
    switch(type) {
      case 'policy': return <Shield className="w-4 h-4 text-blue-500" />;
      case 'endorsement': return <FileText className="w-4 h-4 text-purple-500" />;
      default: return null;
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'General Liability': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Workers Compensation': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const resetDemo = () => {
    setSelectedItem(null);
    setShowVectors(false);
    setSearchResults([]);
    setItems(policyItems);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-green-100 min-h-screen">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Policy Content Intent Vector Database</h1>
        <p className="text-center text-gray-600 mb-8">See how AI understands the intent and purpose behind different policy provisions</p>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Side - Policy Items */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Search className="w-5 h-5" />
              Insurance Policy Database
            </h2>
            
            <div className="space-y-3">
              {items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleItemSelect(item)}
                  className={`w-full p-4 border-2 rounded-lg transition-all duration-300 text-left ${
                    selectedItem?.id === item.id 
                      ? 'border-blue-500 bg-blue-50 shadow-md' 
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {getIcon(item.type)}
                    <div className="flex-1">
                      <span className="font-medium text-gray-800 block">{item.name}</span>
                      <span className="text-sm text-gray-600">{item.content}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`inline-block px-2 py-1 rounded-full text-xs border ${getCategoryColor(item.category)}`}>
                      {item.category}
                    </div>
                    <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      Intent: {item.intent}
                    </div>
                  </div>
                  
                  {showVectors && (
                    <div className="mt-3">
                      <div className="text-xs text-gray-500 mb-1">Content Intent Vector:</div>
                      <div className="text-xs font-mono bg-gray-100 p-2 rounded">
                        [{item.vector.map(v => v.toFixed(1)).join(', ')}]
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Right Side - Search Results */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Similar Policy Intent</h2>
            
            {!selectedItem ? (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Select a policy provision to find similar coverage intents</p>
                <p className="text-sm text-gray-500 mt-2">
                  AI understands: coverage purpose, protection scope, beneficiary intent
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-800 mb-2">
                    🔍 Finding provisions with similar intent to: "{selectedItem.name}"
                  </h3>
                  <p className="text-sm text-blue-600">
                    <strong>Intent:</strong> {selectedItem.intent}
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    Vector matching helps identify overlapping coverage, gaps, and related policy provisions
                  </p>
                </div>
                
                {searchResults.map((result, index) => (
                  <div
                    key={result.id}
                    className={`p-4 rounded-lg border-2 ${
                      result.id === selectedItem.id
                        ? 'border-green-400 bg-green-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1">
                        {getIcon(result.type)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{result.name}</span>
                            {result.id === selectedItem.id && (
                              <span className="text-green-600 text-sm font-semibold">(Selected)</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{result.content}</p>
                          <div className="flex flex-wrap gap-2">
                            <div className={`inline-block px-2 py-1 rounded-full text-xs border ${getCategoryColor(result.category)}`}>
                              {result.category}
                            </div>
                            <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              Intent: {result.intent}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold">
                          {(result.similarity * 100).toFixed(1)}% similar
                        </div>
                        <div className="w-24 bg-gray-200 rounded-full h-2 mt-1">
                          <div 
                            className="bg-gradient-to-r from-red-400 to-green-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${result.similarity * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h4 className="font-semibold text-purple-800 mb-2">🎯 Content Intent Analysis</h4>
                  <div className="text-sm text-purple-700 space-y-1">
                    <div><strong>Primary Intent:</strong> {selectedItem?.intent}</div>
                    <div><strong>Coverage Family:</strong> {selectedItem?.category}</div>
                    <div><strong>Related Intents Found:</strong> {searchResults.slice(1, 3).map(r => r.intent).join(', ')}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="mt-8 text-center space-x-4">
          <button
            onClick={resetDemo}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Reset Demo
          </button>
        </div>

        {/* Policy Intent Applications */}
        <div className="mt-6 bg-gradient-to-r from-indigo-50 to-blue-100 border border-indigo-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-indigo-800 mb-3">🎯 Policy Content Intent Applications</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg border border-indigo-200">
              <h4 className="font-semibold text-indigo-700 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Coverage Gap Analysis
              </h4>
              <p className="text-sm text-indigo-600">AI identifies where policy intents don't align with actual risks, revealing coverage gaps</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-indigo-200">
              <h4 className="font-semibold text-indigo-700 mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Policy Optimization
              </h4>
              <p className="text-sm text-indigo-600">Find overlapping coverage intents to eliminate redundancy and reduce premium costs</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-indigo-200">
              <h4 className="font-semibold text-indigo-700 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Endorsement Matching
              </h4>
              <p className="text-sm text-indigo-600">Automatically suggest relevant endorsements based on policy intent and project requirements</p>
            </div>
          </div>
        </div>

        {/* Intent Categories Breakdown */}
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-yellow-800 mb-4">🏗️ Policy Intent Categories</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded border border-yellow-200">
              <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                General Liability Intents
              </h4>
              <ul className="text-sm text-blue-600 space-y-1">
                <li>• Third-party bodily injury protection</li>
                <li>• Property damage coverage</li>
                <li>• Professional liability extension</li>
                <li>• Product liability coverage</li>
                <li>• Contractual obligation assumption</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded border border-yellow-200">
              <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Workers Compensation Intents
              </h4>
              <ul className="text-sm text-green-600 space-y-1">
                <li>• Medical expense coverage</li>
                <li>• Wage replacement benefits</li>
                <li>• Disability compensation</li>
                <li>• Occupational disease protection</li>
                <li>• Return-to-work facilitation</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Real-World Scenarios */}
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-green-800 mb-3">💼 Real Construction Insurance Scenarios</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded border border-green-200">
              <h4 className="font-semibold text-green-700 mb-2">📋 Scenario: Coverage Review</h4>
              <p className="text-sm text-green-600 mb-2">
                Project Owner requests Additional Insured coverage
              </p>
              <p className="text-xs text-gray-600">
                <strong>AI Analysis:</strong> Vector search finds "Additional Insured - Owners/Contractors" endorsement 
                with 94% intent match, plus related "Contractual Liability" coverage for comprehensive protection
              </p>
            </div>
            <div className="bg-white p-4 rounded border border-green-200">
              <h4 className="font-semibold text-green-700 mb-2">🔍 Scenario: Claims Processing</h4>
              <p className="text-sm text-green-600 mb-2">
                Worker injury claim involves both medical costs and wage loss
              </p>
              <p className="text-xs text-gray-600">
                <strong>AI Analysis:</strong> Identifies both "Medical Benefits" (89% match) and "Wage Replacement" (87% match) 
                intents in Workers Comp policy for complete coverage determination
              </p>
            </div>
          </div>
        </div>

        {/* Business Value */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <div className="text-2xl font-bold text-blue-700 mb-1">85%</div>
            <div className="text-sm text-blue-600">Faster policy provision lookup</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <div className="text-2xl font-bold text-green-700 mb-1">67%</div>
            <div className="text-sm text-green-600">Reduction in coverage gaps</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 text-center">
            <div className="text-2xl font-bold text-purple-700 mb-1">2-5x savings</div>
            <div className="text-sm text-purple-600">Annual savings from better matching</div>
          </div>
        </div>

        {/* Technical Explanation */}
        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">🔧 How Content Intent Vectors Work</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">Traditional Keyword Search</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Searches for exact word matches</li>
                <li>• Misses semantic relationships</li>
                <li>• "Bodily injury" ≠ "worker accident"</li>
                <li>• Limited understanding of intent</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">Vector Intent Search</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Understands purpose and meaning</li>
                <li>• Connects related coverage concepts</li>
                <li>• "Injury protection" = "medical coverage"</li>
                <li>• Reveals hidden policy relationships</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyContentIntentDemo;
